"use client"

import React, { useState } from "react"
import { Outlet, useNavigate, useLocation } from "react-router-dom"
import Sidebar from "./Sidebar"
import TopBar from "./TopBar"
import {
  HomeIcon,
  UsersIcon,
  CreditCardIcon,
  KeyIcon,
  EllipsisHorizontalIcon,
  CircleStackIcon,
  SignalIcon,
  RectangleStackIcon,
  ClipboardDocumentListIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import {
  HomeIcon as HomeIconSolid,
  UsersIcon as UsersIconSolid,
  CreditCardIcon as CreditCardIconSolid,
  KeyIcon as KeyIconSolid,
} from "@heroicons/react/24/solid"

const Layout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [moreSheetOpen, setMoreSheetOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed)
  const toggleMobileSidebar = () => setMobileSidebarOpen(!mobileSidebarOpen)
  const closeMobileSidebar = () => setMobileSidebarOpen(false)

  // iOS Primary Tab Items
  const navTabs = [
    { label: "Home", route: "/", icon: HomeIcon, iconActive: HomeIconSolid },
    { label: "Users", route: "/admin/users", icon: UsersIcon, iconActive: UsersIconSolid },
    { label: "Roles", route: "/admin/roles", icon: KeyIcon, iconActive: KeyIconSolid },
    { label: "More", isMore: true, icon: EllipsisHorizontalIcon },
  ]

  // Extra Administration Items in iOS Action Sheet
  const moreItems = [
    { label: "Database Master", route: "/admin/databases", icon: CircleStackIcon, color: "bg-orange-500", desc: "Dynamic MongoDB schemas" },
    { label: "Telephony Operators", route: "/admin/operators", icon: SignalIcon, color: "bg-cyan-500", desc: "SMPP, Karix, Pinnacle" },
    { label: "Dynamic Menus", route: "/admin/menus", icon: RectangleStackIcon, color: "bg-pink-500", desc: "Navigation trees" },
    { label: "Login Logs", route: "/admin/login-logs", icon: ClipboardDocumentListIcon, color: "bg-gray-500", desc: "Audit trail & security" },
  ]

  const isTabActive = (tab) => {
    if (tab.isMore) {
      return moreItems.some((item) => location.pathname.startsWith(item.route))
    }
    if (tab.route === "/") {
      return location.pathname === "/"
    }
    return location.pathname.startsWith(tab.route)
  }

  const isFullBleed =
    location.pathname.startsWith("/admin/users/create") ||
    location.pathname.startsWith("/admin/users/edit") ||
    location.pathname === "/chat" ||
    location.pathname.startsWith("/chat/") ||
    location.pathname === "/workspace/chat" ||
    location.pathname.startsWith("/workspace/chat/");

  return (
    <div className="flex h-screen bg-[#f5f6fa] dark:bg-[#0f111a] font-sans antialiased text-[#232d42] dark:text-gray-100 overflow-hidden">
      {/* Desktop / Tablet Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileSidebarOpen}
        onClose={closeMobileSidebar}
        onToggleCollapse={toggleSidebar}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        {/* Modern Top Bar */}
        <TopBar
          onToggleSidebar={toggleSidebar}
          onToggleMobileSidebar={toggleMobileSidebar}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          mobileOpen={mobileSidebarOpen}
          onClose={closeMobileSidebar}
        />

        {/* Scrollable Content View with Safe Padding */}
        <main
          className={`flex-1 min-w-0 ${
            isFullBleed
              ? "overflow-hidden p-0 h-full flex flex-col"
              : "overflow-y-auto px-4 py-5 sm:px-6 sm:py-6 pb-28 lg:pb-8"
          }`}
        >
          <div className={isFullBleed ? "w-full h-full flex-1 flex flex-col min-h-0" : "max-w-7xl mx-auto w-full"}>
            <Outlet />
          </div>
        </main>

        {/* ── Floating Bottom Tab Bar (Visible on Mobile & Tablet < 1024px, hidden on full-bleed pages like Chat) ── */}
        {!isFullBleed && (
          <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-3 pt-1 pointer-events-none">
            <div className="max-w-md mx-auto pointer-events-auto">
              <nav className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl px-2 py-1.5 shadow-lg border border-gray-200 dark:border-gray-700 flex items-center justify-around">
                {navTabs.map((tab, idx) => {
                  const active = isTabActive(tab)
                  const IconComponent = active && tab.iconActive ? tab.iconActive : tab.icon

                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        if (tab.isMore) {
                          setMoreSheetOpen(true)
                        } else {
                          navigate(tab.route)
                          setMoreSheetOpen(false)
                        }
                      }}
                      className={`flex flex-col items-center justify-center py-1 px-3 rounded-xl transition-all ${
                        active
                          ? "text-[#007AFF] dark:text-blue-400 font-bold"
                          : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                      }`}
                    >
                      <div className="relative">
                        <IconComponent className="w-5 h-5" />
                        {active && (
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-[#007AFF] rounded-full" />
                        )}
                      </div>
                      <span className="text-[10px] mt-0.5 tracking-tight">
                        {tab.label}
                      </span>
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>
        )}

        {/* ── Action Sheet Drawer for "More" Menu on Mobile ── */}
        {moreSheetOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end">
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
              onClick={() => setMoreSheetOpen(false)}
            />
            <div className="relative w-full max-w-lg mx-auto bg-white dark:bg-gray-800 rounded-t-3xl p-6 shadow-2xl border-t border-gray-200 dark:border-gray-700 animate-fade-in z-10 pb-8">
              {/* Grab Handle */}
              <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-4" />

              <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800 mb-4">
                <div className="text-left">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">Administration</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">All administrative management portals</p>
                </div>
                <button
                  onClick={() => setMoreSheetOpen(false)}
                  className="p-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-gray-700 dark:hover:text-white ios-tap"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-2.5">
                {moreItems.map((item, i) => {
                  const Icon = item.icon
                  const isCurrent = location.pathname.startsWith(item.route)
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        navigate(item.route)
                        setMoreSheetOpen(false)
                      }}
                      className={`flex items-center gap-3.5 p-3 rounded-2xl text-left ios-tap transition-all ${
                        isCurrent
                          ? "bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/60"
                          : "bg-gray-50 dark:bg-[#2C2C2E]/60 hover:bg-gray-100 dark:hover:bg-[#2C2C2E]"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-2xl ${item.color} text-white flex items-center justify-center shadow-sm flex-shrink-0`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{item.label}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.desc}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Layout